import React, { useEffect, useState, useCallback } from "react";

/* CookieConsent
   – Bottom-pinned banner shown on first visit
   – "Manage preferences" opens a modal with per-category toggles
   – Stores choice in localStorage as bsl_cookie_consent (v1)
   – Gates third-party loaders so they only run after consent:
       * Crisp live chat   → Functional category
       * Google Analytics  → Analytics category (only fires if a
                              measurement ID is present in env)
   – Exposes window.openCookieSettings() so footer / policy
     pages can re-open the modal at any time. */

const STORAGE_KEY = "bsl_cookie_consent_v1";
const CRISP_ID = "18797922-ac15-4b0c-ac95-695743192b76";
// CRA statically replaces process.env.REACT_APP_* with a string literal at build
// time, so this bare access is safe in the browser. Do NOT wrap it in a
// `typeof process !== "undefined"` guard: `process` is undefined in the browser,
// so that guard evaluated false and left GA_ID null - GA never loaded.
const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";

const readConsent = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeConsent = (value) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (e) {
    /* ignore – private browsing etc. */
  }
};

const loadCrisp = () => {
  if (typeof window === "undefined") return;
  if (window.$crisp && window.$crisp.is) return; // already loaded
  window.$crisp = window.$crisp || [];
  window.CRISP_WEBSITE_ID = CRISP_ID;
  if (document.getElementById("crisp-loader-script")) return;
  const s = document.createElement("script");
  s.id = "crisp-loader-script";
  s.src = "https://client.crisp.chat/l.js";
  s.async = true;
  document.head.appendChild(s);
};

const unloadCrisp = () => {
  if (typeof window === "undefined") return;
  try {
    if (Array.isArray(window.$crisp)) {
      window.$crisp.push(["do", "chat:hide"]);
    }
  } catch (e) {
    /* ignore */
  }
};

const loadGA = () => {
  if (typeof window === "undefined" || !GA_ID) return;
  if (window.__bslGaLoaded) return;
  window.__bslGaLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_ID, { anonymize_ip: true });
};

/* The /embed/* routes (and /sign-of-the-day/embed) are chrome-less widgets
   embedded in third-party sites via an iframe. They share our origin, so a
   consent stored on the main site would otherwise pull the Crisp chat button
   (and analytics) into the widget. Keep widgets clean: load nothing and show
   no cookie banner in that context. */
const isEmbedPath = () => {
  if (typeof window === "undefined") return false;
  return /(^|\/)embed(\/|$)/.test(window.location.pathname);
};

const applyConsent = (consent) => {
  if (!consent) return;
  // In an embedded widget, never load the chat or analytics.
  if (isEmbedPath()) {
    unloadCrisp();
    return;
  }
  if (consent.functional) loadCrisp();
  else unloadCrisp();
  if (consent.analytics) loadGA();
};

const CookieConsent = () => {
  const [consent, setConsent] = useState(undefined); // undefined = not hydrated yet
  const [bannerOpen, setBannerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState({ functional: false, analytics: false });

  // Hydrate from localStorage and apply stored consent
  useEffect(() => {
    const stored = readConsent();
    setConsent(stored);
    // Embedded widgets: no banner, no chat, no analytics (and hide Crisp if a
    // shared-origin consent somehow pulled it in).
    if (isEmbedPath()) {
      unloadCrisp();
      return;
    }
    if (!stored) {
      setBannerOpen(true);
    } else {
      applyConsent(stored);
      setDraft({
        functional: !!stored.functional,
        analytics: !!stored.analytics,
      });
    }
  }, []);

  // Expose a global trigger so footer / policy pages can reopen
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.openCookieSettings = () => {
      const stored = readConsent();
      setDraft({
        functional: !!(stored && stored.functional),
        analytics: !!(stored && stored.analytics),
      });
      setModalOpen(true);
      setBannerOpen(false);
    };
    return () => {
      try {
        delete window.openCookieSettings;
      } catch (e) {
        window.openCookieSettings = undefined;
      }
    };
  }, []);

  const persist = useCallback((value) => {
    const next = {
      essential: true,
      functional: !!value.functional,
      analytics: !!value.analytics,
      ts: new Date().toISOString(),
      v: 1,
    };
    writeConsent(next);
    setConsent(next);
    applyConsent(next);
  }, []);

  const acceptAll = () => {
    persist({ functional: true, analytics: true });
    setBannerOpen(false);
    setModalOpen(false);
  };
  const rejectAll = () => {
    persist({ functional: false, analytics: false });
    setBannerOpen(false);
    setModalOpen(false);
  };
  const saveDraft = () => {
    persist(draft);
    setBannerOpen(false);
    setModalOpen(false);
  };
  const openManage = () => {
    setDraft({
      functional: !!(consent && consent.functional),
      analytics: !!(consent && consent.analytics),
    });
    setModalOpen(true);
  };

  // Don’t render until hydrated to avoid SSR/snapshot flash
  if (consent === undefined) return null;
  // Embedded widgets show no cookie chrome at all.
  if (isEmbedPath()) return null;

  return (
    <>
      {bannerOpen && (
        <div
          role="dialog"
          aria-live="polite"
          aria-label="Cookie preferences"
          data-testid="cookie-banner"
          style={{
            position: "fixed",
            left: "12px",
            right: "12px",
            bottom: "12px",
            zIndex: 9999,
            background: "#0B1B2E",
            color: "#fff",
            borderRadius: "14px",
            boxShadow: "0 12px 40px rgba(11,27,46,0.35)",
            padding: "12px 14px",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "10px 16px",
            maxWidth: "1080px",
            margin: "0 auto",
          }}
        >
          {/* Kept compact so it does not hide the page on a phone (it sat over
              the pricing on /enrol). Reject and Accept are the same size. */}
          <div
            style={{
              flex: "1 1 320px",
              fontSize: "0.85rem",
              lineHeight: 1.4,
              opacity: 0.92,
            }}
          >
            With your permission we use cookies for live chat and Google
            Analytics. No advertising, ever.{" "}
            <a
              href="/cookies-policy"
              style={{ color: "#9cc4ff", textDecoration: "underline" }}
            >
              Cookies Policy
            </a>
            {" · "}
            <button
              type="button"
              onClick={openManage}
              data-testid="cookie-banner-manage"
              style={{
                background: "transparent",
                color: "#9cc4ff",
                border: "none",
                padding: 0,
                font: "inherit",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              Manage preferences
            </button>
          </div>
          <div
            style={{
              display: "flex",
              flex: "1 1 240px",
              justifyContent: "flex-end",
            }}
          >
            <div style={{ display: "flex", gap: "10px", flex: "1 1 auto", maxWidth: 340 }}>
              <button
                type="button"
                onClick={rejectAll}
                data-testid="cookie-banner-reject"
                style={{
                  flex: "1 1 0",
                  background: "#243248",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: "10px",
                  padding: "9px 12px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-banner-accept"
                style={{
                  flex: "1 1 0",
                  background: "#1f7afc",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "9px 12px",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Cookie preferences"
          data-testid="cookie-modal"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10000,
            background: "rgba(11,27,46,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setModalOpen(false);
          }}
        >
          <div
            style={{
              background: "#fff",
              color: "#0B1B2E",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "520px",
              padding: "26px",
              boxShadow: "0 18px 60px rgba(0,0,0,0.25)",
            }}
          >
            <h2
              style={{
                margin: "0 0 6px",
                fontSize: "1.35rem",
                fontWeight: 700,
              }}
            >
              Cookie preferences
            </h2>
            <p style={{ margin: "0 0 18px", color: "#4b5868" }}>
              Choose which optional cookies you’re happy for us to set. You
              can change your mind any time on the{" "}
              <a href="/cookies-policy">Cookies Policy</a> page.
            </p>

            <div
              style={{
                border: "1px solid #e3e8ee",
                borderRadius: "12px",
                padding: "14px 16px",
                marginBottom: "12px",
                background: "#f8fafc",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <strong>Strictly necessary</strong>
                <span style={{ color: "#4b5868", fontSize: "0.9rem" }}>
                  Always on
                </span>
              </div>
              <div
                style={{
                  fontSize: "0.92rem",
                  color: "#4b5868",
                  marginTop: 4,
                  lineHeight: 1.45,
                }}
              >
                Required for the website, shop and learner area to function.
              </div>
            </div>

            <label
              style={{
                display: "flex",
                gap: "14px",
                alignItems: "flex-start",
                border: "1px solid #e3e8ee",
                borderRadius: "12px",
                padding: "14px 16px",
                marginBottom: "12px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={draft.functional}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, functional: e.target.checked }))
                }
                data-testid="cookie-toggle-functional"
                style={{ marginTop: 4, width: 18, height: 18 }}
              />
              <span>
                <strong>Functional (Crisp live chat)</strong>
                <span
                  style={{
                    display: "block",
                    fontSize: "0.92rem",
                    color: "#4b5868",
                    marginTop: 4,
                    lineHeight: 1.45,
                  }}
                >
                  Lets the chat widget remember your conversation with our
                  team so you can pick it up again later.
                </span>
              </span>
            </label>

            <label
              style={{
                display: "flex",
                gap: "14px",
                alignItems: "flex-start",
                border: "1px solid #e3e8ee",
                borderRadius: "12px",
                padding: "14px 16px",
                marginBottom: "18px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={draft.analytics}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, analytics: e.target.checked }))
                }
                data-testid="cookie-toggle-analytics"
                style={{ marginTop: 4, width: 18, height: 18 }}
              />
              <span>
                <strong>Analytics (Google Analytics 4)</strong>
                <span
                  style={{
                    display: "block",
                    fontSize: "0.92rem",
                    color: "#4b5868",
                    marginTop: 4,
                    lineHeight: 1.45,
                  }}
                >
                  Aggregated, IP-anonymised stats about which pages are
                  useful. Not used inside the paid learner area.
                </span>
              </span>
            </label>

            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                data-testid="cookie-modal-close"
                style={{
                  background: "transparent",
                  color: "#0B1B2E",
                  border: "1px solid #cdd6e0",
                  borderRadius: "10px",
                  padding: "10px 16px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={rejectAll}
                data-testid="cookie-modal-reject"
                style={{
                  background: "#f0f3f7",
                  color: "#0B1B2E",
                  border: "1px solid #cdd6e0",
                  borderRadius: "10px",
                  padding: "10px 16px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={saveDraft}
                data-testid="cookie-modal-save"
                style={{
                  background: "#1f7afc",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "10px 18px",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Save preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
